import React from 'react'
import { useNavigate } from 'react-router-dom'

import { IconType } from '@factorialco/f0-react'
import { ProcurementTypeItemFragment } from 'generated/resources/graphql'
import { CreateProcurementTypeFormDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface UseProcurementTypeActionsProps {
  onEditProcurementTypeForm: (procurementType: ProcurementTypeItemFragment) => void
  onEditProcurementType: (procurementType: ProcurementTypeItemFragment) => void
  onToggle: (procurementType: ProcurementTypeItemFragment) => void
  onDelete: (procurementType: ProcurementTypeItemFragment) => void
  onDuplicate: (procurementType: ProcurementTypeItemFragment) => void
}

interface ProcurementTypeAction {
  label: string
  icon?: IconType
  variant?: 'default' | 'secondary' | 'danger'
  type: 'primary' | 'secondary' | 'other'
  onClick: () => void
}

export const useProcurementTypeActions = ({
  onEditProcurementTypeForm,
  onEditProcurementType,
  onToggle,
  onDelete,
  onDuplicate,
}: UseProcurementTypeActionsProps) => {
  const { access } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [createProcurementTypeFormMutation] = useMutation(CreateProcurementTypeFormDocument, {
    extractorFn: (data) => ({
      ...data.formsMutations.createFormsForm,
    }),
  })

  const createProcurementTypeForm = React.useCallback(
    async (procurementType: ProcurementTypeItemFragment) => {
      const result = await createProcurementTypeFormMutation({
        variables: {
          companyId: procurementType.companyId,
          description: procurementType.description || '',
          formType: 'custom',
          resourceId: procurementType.id.toString(),
          resourceType: 'Procurement::Type',
          title: procurementType.name,
          anonymous: false,
          authorId: access.id,
        },
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: 'errors_procurement_create_form_error',
        })
      })
      if (handled.success) {
        const searchParams = new URLSearchParams({ tab: 'form' })
        navigate({
          pathname: navigation.settings.procurement.requestType.id.fullPath({
            id: procurementType.id,
          }),
          search: searchParams.toString(),
        })
      }
    },
    [createProcurementTypeFormMutation, access.id, navigate, navigation]
  )
  // Action availability rules

  const canCreateForm = React.useCallback(
    (procurementType: ProcurementTypeItemFragment) => {
      return Boolean(!procurementType.formsConnection?.nodes?.[0]?.id && onEditProcurementTypeForm)
    },
    [onEditProcurementTypeForm]
  )

  const canEdit = React.useCallback(
    (procurementType: ProcurementTypeItemFragment) =>
      Boolean(!procurementType.identifier && onEditProcurementType),
    [onEditProcurementType]
  )

  const canToggle = React.useCallback(
    (procurementType: ProcurementTypeItemFragment) =>
      Boolean(
        (procurementType.identifier === 'software' || !procurementType.identifier) && onToggle
      ),
    [onToggle]
  )

  const canDelete = React.useCallback(
    (procurementType: ProcurementTypeItemFragment) => Boolean(!procurementType.identifier),
    []
  )
  const canDuplicate = React.useCallback(
    (procurementType: ProcurementTypeItemFragment) => Boolean(!procurementType.identifier),
    []
  )

  // Action creators
  const createEditFormAction = React.useCallback(
    (procurementType: ProcurementTypeItemFragment): ProcurementTypeAction => ({
      label: i18n.t('procurement.purchase_requests.settings.types.actions_new.create_form'),
      variant: 'secondary',
      type: 'other',
      onClick: () => createProcurementTypeForm(procurementType),
    }),
    [createProcurementTypeForm]
  )

  const createEditAction = React.useCallback(
    (procurementType: ProcurementTypeItemFragment): ProcurementTypeAction => ({
      label: i18n.t('procurement.purchase_requests.settings.types.actions_new.edit'),
      variant: 'secondary',
      type: 'other',
      onClick: () => onEditProcurementType(procurementType),
    }),
    [onEditProcurementType]
  )

  const createToggleAction = React.useCallback(
    (procurementType: ProcurementTypeItemFragment): ProcurementTypeAction => ({
      label: procurementType.enabled
        ? i18n.t('procurement.purchase_requests.settings.types.actions_new.disable')
        : i18n.t('procurement.purchase_requests.settings.types.actions_new.enable'),
      type: 'other',
      onClick: () => onToggle(procurementType),
    }),
    [onToggle]
  )

  const createDuplicateAction = React.useCallback(
    (procurementType: ProcurementTypeItemFragment): ProcurementTypeAction => ({
      label: i18n.t('procurement.purchase_requests.settings.types.actions_new.duplicate'),
      type: 'other',
      onClick: () => onDuplicate(procurementType),
    }),
    [onDuplicate]
  )

  const createDeleteAction = React.useCallback(
    (procurementType: ProcurementTypeItemFragment): ProcurementTypeAction => ({
      label: i18n.t('procurement.purchase_requests.settings.types.actions_new.delete'),
      variant: 'danger',
      type: 'other',
      onClick: () => onDelete(procurementType),
    }),
    [onDelete]
  )

  const getActions = React.useCallback(
    (procurementType: ProcurementTypeItemFragment): ProcurementTypeAction[] => {
      const actions: ProcurementTypeAction[] = []

      // // This action is available for all procurement types
      if (canCreateForm(procurementType)) {
        actions.push(createEditFormAction(procurementType))
      }

      // Add rest of actions when no identifier is present
      if (canEdit(procurementType)) {
        actions.push(createEditAction(procurementType))
      }

      // Toggle action - for software and custom types
      if (canToggle(procurementType)) {
        actions.push(createToggleAction(procurementType))
      }

      // Delete action - only for custom types (no identifier)
      if (canDelete(procurementType)) {
        actions.push(createDeleteAction(procurementType))
      }

      if (canDuplicate(procurementType)) {
        actions.push(createDuplicateAction(procurementType))
      }

      return actions
    },
    [
      canCreateForm,
      canEdit,
      canToggle,
      canDelete,
      canDuplicate,
      createEditFormAction,
      createEditAction,
      createToggleAction,
      createDeleteAction,
      createDuplicateAction,
    ]
  )

  return { getActions }
}
